import { initI18n } from '@/lib/i18n';

/**
 * The `session` and `commission` i18n namespaces (BP-05 §6).
 *
 * Registered from the feature rather than added to `src/lib/i18n/resources.ts`, for the
 * reason `features/scheduling/strings.ts` gives: a feature package owns its copy, and the
 * shared bundle stays the chrome's.
 *
 * **No key here interpolates a client's name or number.** The timer header takes a first
 * name the api already decided to disclose, and the profile card shows the api's mask
 * (§9); nothing in this bundle can widen that.
 */
export const SESSION_NAMESPACE = 'session';
export const COMMISSION_NAMESPACE = 'commission';

export const sessionStrings = {
  nav: {
    station: 'Station',
    commissions: 'Commissions',
    commissionRules: 'Commissions',
  },
  station: {
    title: 'Station',
    empty: 'No session at this station — pick one from the queue',
    queue: 'Queue',
    elapsed: 'Elapsed',
    paused: 'Paused',
    offline: 'Offline — notes are queued, everything else waits for the network',
    conflict: 'Somebody else wrote first. The session has been reloaded.',
    retry: 'Try again',
    service: 'Service',
    primary: 'With',
    wakeLock: 'Keeping the screen awake',
  },
  step: {
    legend: 'Step',
    running: 'Running',
    stop: 'Stop step',
    prep: 'Prep',
    removal: 'Removal',
    base: 'Base',
    colour: 'Colour',
    art: 'Art',
    top: 'Top',
    cure: 'Cure',
    dry: 'Dry',
    other: 'Other',
  },
  status: {
    ready: 'Ready',
    in_progress: 'In progress',
    paused: 'Paused',
    completed: 'Completed',
    abandoned: 'Abandoned',
  },
  action: {
    pause: 'Pause',
    resume: 'Resume',
    handover: 'Hand over',
    materials: 'Materials',
    notes: 'Notes',
    complete: 'Complete',
    abandon: 'Abandon',
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
  },
  handover: {
    title: 'Hand over',
    to: 'To',
    role: 'Role',
    primary: 'Primary',
    assist: 'Assist',
    reason: 'Reason',
    confirm: 'Hand over',
    none: 'Nobody else is on the roster',
  },
  materials: {
    title: 'Materials',
    none: 'Nothing recorded yet',
    sku: 'Product',
    qty: 'Quantity',
    unit: 'Unit',
    bom: 'Planned',
    variance: 'Variance',
    step: 'Step',
    add: 'Add',
    search: 'Search products',
    barcode: 'Barcode',
    scan: 'Find',
    remove: 'Remove',
    removed: 'Removed',
    repeat: 'Repeat last set',
    repeatEdited: 'These materials have already been edited, so the last set was not copied.',
    repeatNone: 'This client has no previous set to copy.',
    withdrawn: 'Withdrawn since it was planned',
    sell: 'Sold to the client',
    more: 'Add a product',
  },
  notes: {
    title: 'Notes',
    none: 'No notes',
    body: 'Note',
    kind: 'Visibility',
    tech: 'Internal',
    client_visible: 'Client can see this',
    add: 'Add note',
    queued: 'Queued — it will be sent when the network returns',
  },
  complete: {
    title: 'Complete the session',
    confirm: 'I have checked the materials',
    confirmRequired: 'Confirm the materials before completing.',
    variance: 'Variance against the plan',
    inventory: 'The stock room did not answer, so nothing was taken. The session is still open.',
    submit: 'Complete',
  },
  abandon: {
    title: 'Abandon the session',
    reason: 'What happened?',
    consume: 'Still take the materials off the shelf',
    frontdesk: 'The front desk has confirmed',
    submit: 'Abandon',
  },
  queue: {
    title: 'Queue',
    none: 'Nothing waiting',
    open: 'Open',
    anyStation: 'Any station',
    thisStation: 'This station',
  },
  detail: {
    title: 'Session',
    steps: 'Steps',
    staff: 'Who worked on it',
    materials: 'Materials',
    notes: 'Notes',
    events: 'Event log',
    minutes: '{{minutes}} min',
    running: 'still running',
    from: 'From',
    to: 'To',
    seq: '#',
    type: 'Event',
    at: 'At',
    actor: 'By',
    payload: 'Payload',
  },
  profile: {
    title: 'Client',
    phone: 'Phone',
    shape: 'Shape',
    length: 'Length',
    finish: 'Finish',
    allergies: 'Allergies',
    sensitivities: 'Sensitivities',
    technician: 'Prefers',
    lastSets: 'Last sets',
    none: 'Nothing on file yet',
    anonymous: 'Walk-in — no client record',
  },
  rating: {
    title: 'How did we do?',
    score: 'Stars',
    star: '{{count}} star',
    star_other: '{{count}} stars',
    text: 'Anything you would like to add?',
    submit: 'Send',
    thanks: 'Thank you.',
    expired: 'This link has expired.',
  },
} as const;

export const commissionStrings = {
  rules: {
    title: 'Commission rules',
    none: 'No rules yet — nobody earns commission',
    name: 'Name',
    scope: 'Applies to',
    basis: 'Basis',
    split: 'Split',
    priority: 'Priority',
    validFrom: 'From',
    validTo: 'To',
    active: 'Active',
    add: 'New rule',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    percent: 'Percent (%)',
    fixed: 'Fixed (₹)',
    tiers: 'Tiers',
    tierFrom: 'From (₹)',
    tierPercent: 'Percent (%)',
    addTier: 'Add a tier',
    removeTier: 'Remove',
    primaryShare: 'Primary share (%)',
    staff: 'Technician',
    role: 'Role',
    scopeId: 'Which one',
    invalid: 'That rule does not add up.',
  },
  scope: {
    service: 'One service',
    category: 'A category',
    product: 'Retail products',
    all: 'Everything',
  },
  appliesTo: {
    staff: 'One technician',
    role: 'A role',
  },
  basis: {
    percent_net: 'Percent of net',
    fixed_per_unit: 'Fixed per unit',
    tiered_monthly: 'Monthly tiers',
  },
  split: {
    by_minutes: 'By minutes worked',
    fixed: 'Fixed primary share',
  },
  preview: {
    title: 'Preview on an invoice',
    invoice: 'Invoice id',
    run: 'Preview',
    none: 'Nobody earns anything on that invoice',
    noSource: 'Billing is not installed yet, so there is no invoice to preview against.',
    notFound: 'No such invoice.',
    staff: 'Technician',
    rule: 'Rule',
    base: 'Base (₹)',
    share: 'Share (%)',
    amount: 'Amount (₹)',
    unmatched: 'No rule',
  },
  statement: {
    title: 'Commissions',
    period: 'Month',
    staff: 'Technician',
    mine: 'My statement',
    show: 'Show',
    none: 'Nothing earned in that month',
    earn: 'Earned',
    reversal: 'Reversed',
    net: 'Net',
    csv: 'Download CSV',
    invoice: 'Invoice',
    session: 'Session',
    rule: 'Rule',
    basis: 'Basis',
    base: 'Base (₹)',
    share: 'Share (%)',
    amount: 'Amount (₹)',
    kind: 'Kind',
    computedAt: 'Computed',
    forbidden: 'You may only read your own statement.',
    earnKind: 'Earned',
    reversalKind: 'Reversed',
  },
} as const;

/** Add both bundles to the shared i18next instance. Idempotent. */
export function registerSessionStrings(): void {
  const i18n = initI18n();
  i18n.addResourceBundle('en', SESSION_NAMESPACE, sessionStrings, true, false);
  i18n.addResourceBundle('en', COMMISSION_NAMESPACE, commissionStrings, true, false);
}

registerSessionStrings();
